import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Modal, Alert, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Breadcrumb from '../components/Breadcrumb';
import apiService from '../services/apiService';
import Swal from 'sweetalert2';
import './test.css';

const ManageSalesPersons = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    base_commission: 20, // percentage input
    bonus: 5, // percentage input
    threshold: 1000000,
  });
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', base_commission: 0, bonus: 0, threshold: 0 });
  const [savingEdit, setSavingEdit] = useState(false);

  const breadcrumbItems = [
      { label: 'Commissions', href: null},
      { label: 'Management', href: null},
      { label: 'Sales Person', href: null }
  ];

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getSalesPersonsManage();
      setItems(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      setError(e.message || 'Failed to load salespersons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Helpers
  const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return 'N/A';
    const num = Number(val);
    if (Number.isNaN(num)) return val;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      // Normalize percentage inputs to decimals for API
      const payload = {
        name: createForm.name,
        email: createForm.email || null,
        base_commission: Number(createForm.base_commission) / 100,
        bonus: Number(createForm.bonus) / 100,
        threshold: Number(createForm.threshold) || 0,
      };
      await apiService.createSalesPerson(payload);
      setCreateForm({ name: '', base_commission: 20, bonus: 5, threshold: 1000000 });
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to create salesperson');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '',
      email: item.email || '',
      // Convert decimals to percentages for UI
      base_commission: typeof item.base_commission === 'number' ? (item.base_commission * 100) : 0,
      bonus: typeof item.bonus === 'number' ? (item.bonus * 100) : 0,
      threshold: item.threshold ?? 0,
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        email: editForm.email || null,
        base_commission: Number(editForm.base_commission) / 100,
        bonus: Number(editForm.bonus) / 100,
        threshold: Number(editForm.threshold) || 0,
      };
      await apiService.updateSalesPerson(editItem.id, payload);
      setShowEdit(false);
      setEditItem(null);
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to update salesperson');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: `Delete ${item.name}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await apiService.deleteSalesPerson(item.id);
      await Swal.fire({ title: 'Deleted', text: `${item.name} removed successfully.`, icon: 'success', timer: 1200, showConfirmButton: false });
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to delete salesperson');
    }
  };

  return (
    <div className="py-3">
      <Breadcrumb items={breadcrumbItems} />

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">Sales Persons</h2>
              <p className="page-subtitle text-muted mb-0">Manage salesperson records</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                <Button
                  variant={showCreate ? 'primary' : 'outline-primary'}
                  className="d-flex align-items-center gap-2"
                  onClick={() => setShowCreate(true)}
                >
                  <i className="fas fa-plus"></i> New Sales Person
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            {/* <span className="text-muted small">Total: {items.length}</span> */}
          </div>
          {loading ? (
            <div className="text-muted small">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-muted small">No salespersons available.</div>
          ) : (
            <div className="containesr py-2">
              <div className="row g-3">
                {items.map((s, index) => (
                  <div className="col-md-6 col-lg-3" key={s.id || index}>
                    <div className="agent-card p-3">
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="avatar-gradient">
                            <i className="fas fa-fw fa-user text-white fs-5"></i>
                          </div>
                          <div>
                            <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                              {s.name}
                            </h5>
                            {s.email && (
                              <p className="text-muted small mb-0">{s.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {/* Edit */}
                          <button
                            className="btn btn-light btn-sm edit-btn"
                            onClick={() => openEdit(s)}
                            aria-label={`Edit ${s.name}`}
                          >
                            <i className="fas fa-fw fa-pen"></i>
                          </button>
                        </div>
                      </div>

                      <hr className="my-2" />

                      {/* Body */}
                      <div className="card-body p-0">
                        <div className="commission-box">
                          <p className="small mb-1">Base Commission</p>
                          <p className="fw-bold fs-6 mb-0">
                            {typeof s.base_commission === 'number' ? `${(s.base_commission * 100).toFixed(2)}%` : s.base_commission}
                          </p>
                        </div>

                        <div className="small text-muted">
                          <div className="d-flex justify-content-between py-1 border-top">
                            <span>HiLi</span>
                            <span className="fw-semibold text-dark">{typeof s.bonus === 'number' ? `${(s.bonus * 100).toFixed(2)}%` : s.bonus}</span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-top">
                            <span>Threshold</span>
                            <span className="fw-semibold text-dark">{formatCurrency(s.threshold)}</span>
                          </div>
                        </div>

                        {/* Bottom actions */}
                        <div className="mt-3 d-flex justify-content-end">
                          <button
                            className="btn btn-danger btn-sm delete-btn"
                            onClick={() => handleDelete(s)}
                            aria-label={`Delete ${s.name}`}
                          >
                            <i className="fas fa-fw fa-trash"></i>
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* Create Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Sales Person</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreate} className="row g-3">
            <Form.Group as={Col} md={6} controlId="create_name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Enter name"
                required
              />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="Enter email"
              />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_threshold">
              <Form.Label>Threshold</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={createForm.threshold}
                onChange={(e) => setCreateForm({ ...createForm, threshold: parseInt(e.target.value || '0', 10) })}
              />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_base_commission">
              <Form.Label>Base Commission</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={createForm.base_commission}
                  onChange={(e) => setCreateForm({ ...createForm, base_commission: e.target.value })}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_bonus">
              <Form.Label>HiLi</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={createForm.bonus}
                  onChange={(e) => setCreateForm({ ...createForm, bonus: e.target.value })}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button type="submit" variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Sales Person</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="row g-3">
            <Form.Group as={Col} md={6} controlId="edit_name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_email">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_base_commission">
              <Form.Label>Base Commission</Form.Label>
              <InputGroup>
                <Form.Control type="number" step="0.01" min="0" max="100" value={editForm.base_commission} onChange={(e) => setEditForm({ ...editForm, base_commission: e.target.value })} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_bonus">
              <Form.Label>HiLi</Form.Label>
              <InputGroup>
                <Form.Control type="number" step="0.01" min="0" max="100" value={editForm.bonus} onChange={(e) => setEditForm({ ...editForm, bonus: e.target.value })} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_threshold">
              <Form.Label>Threshold</Form.Label>
              <Form.Control type="number" step="1" value={editForm.threshold} onChange={(e) => setEditForm({ ...editForm, threshold: parseInt(e.target.value || '0', 10) })} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageSalesPersons;